import React, { useState, useEffect } from 'react';
import {
  Modal, Box, IconButton, Typography, Grid,
  FormControlLabel, Switch, Button, Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { MuiColorInput } from 'mui-color-input';
import WeatherWidget from '../../WeatherWidget';
import useFontFamilies from '../../../hooks/useFontFamilies';

// ── Same pattern as CaptionSettingsModal ─────────────────────────────────────
const ModalContent = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  boxShadow: theme.shadows[24],
  width: '90%',
  maxWidth: '1060px',
  maxHeight: '90vh',
  overflowY: 'auto',
  position: 'relative',
}));

const Header = styled(Box)({
  background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
  borderRadius: '12px 12px 0 0',
  padding: '20px 28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const Body = styled(Box)({ padding: '24px 28px' });

const Footer = styled(Box)({
  padding: '16px 28px',
  borderTop: '1px solid #e2e8f0',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '12px',
  background: '#f8fafc',
  borderRadius: '0 0 12px 12px',
});

const SectionCard = styled(Box)({
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '16px',
  marginBottom: '16px',
});

const SectionHeading = styled(Typography)({
  fontWeight: 700,
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  color: '#64748b',
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const FieldLabel = styled('label')({
  fontSize: '12px',
  fontWeight: 600,
  color: '#374151',
  display: 'block',
  marginBottom: '5px',
});

const StyledInput = styled('input')({
  width: '100%',
  padding: '8px 10px',
  borderRadius: '8px',
  border: '1.5px solid #e2e8f0',
  fontSize: '13px',
  outline: 'none',
  transition: 'border-color 0.2s',
  boxSizing: 'border-box',
});

const StyledSelect = styled('select')({
  width: '100%',
  padding: '8px 10px',
  borderRadius: '8px',
  border: '1.5px solid #e2e8f0',
  fontSize: '13px',
  outline: 'none',
  background: 'white',
  cursor: 'pointer',
  boxSizing: 'border-box',
});

const OrderItem = styled(Box)(({ isDragging }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: isDragging ? '#ede9fe' : '#ffffff',
  border: `1.5px solid ${isDragging ? '#8b5cf6' : '#e2e8f0'}`,
  borderRadius: '8px',
  padding: '8px 12px',
  marginBottom: '6px',
  cursor: 'grab',
  transition: 'all 0.15s',
  userSelect: 'none',
  '&:hover': { borderColor: '#8b5cf6', background: '#faf5ff' },
}));

const ToggleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  background: '#ffffff',
  border: '1.5px solid #e2e8f0',
  borderRadius: '8px',
  marginBottom: '6px',
  '&:hover': { borderColor: '#6366f1', background: '#fafafe' },
});

// ── Constants ─────────────────────────────────────────────────────────────────

const SECTION_META = {
  clock: { label: 'Clock & Location', emoji: '🕐', color: '#6366f1' },
  current: { label: 'Current Weather', emoji: '🌡️', color: '#0ea5e9' },
  forecast: { label: 'Forecast', emoji: '📅', color: '#10b981' },
};

const DEFAULT_SETTINGS = {
  enabled: false,
  weatherApiKey: '',
  latitude: null,
  longitude: null,
  forecastDays: 3,
  temperatureUnit: 'both',
  celsiusFirst: true,
  showLocation: false,
  showClock: true,
  showCurrentWeather: true,
  showForecast: true,
  widgetOrder: ['clock', 'current', 'forecast'],
  positionLeft: null,
  positionRight: 2,
  positionTop: 2,
  positionBottom: null,
  horizontalAlign: 'right',
  backgroundColor: '#ffffff',
  backgroundOpacity: 80,
  textColor: '#1a202c',
  subTextColor: '#718096',
  padding: 16,
  borderRadius: 12,
  width: 0,
  fontFamily: 'inherit',
  fontSize: 14,
  iconSize: 40,
  forecastIconSize: 28,
  shadowH: 0,
  shadowV: 4,
  shadowBlur: 12,
  shadowSpread: 0,
  shadowColor: '#000000',
  shadowOpacity: 15,
  visibility: {
    splashScreen: true,
    mainMenu: true,
    subMenu: true,
    wayfinderSearchScreen: true,
    avatarScreen: true,
  },
};

// ── City data ─────────────────────────────────────────────────────────────────
const CITIES = [
  { city: "New York City", country: "USA", lat: 40.7128, lng: -74.0060 },
  { city: "Los Angeles", country: "USA", lat: 34.0522, lng: -118.2437 },
  { city: "Chicago", country: "USA", lat: 41.8781, lng: -87.6298 },
  { city: "Houston", country: "USA", lat: 29.7604, lng: -95.3698 },
  { city: "Phoenix", country: "USA", lat: 33.4484, lng: -112.0740 },
  { city: "Philadelphia", country: "USA", lat: 39.9526, lng: -75.1652 },
  { city: "San Antonio", country: "USA", lat: 29.4241, lng: -98.4936 },
  { city: "San Diego", country: "USA", lat: 32.7157, lng: -117.1611 },
  { city: "Dallas", country: "USA", lat: 32.7767, lng: -96.7970 },
  { city: "San Jose", country: "USA", lat: 37.3382, lng: -121.8863 },
  { city: "Austin", country: "USA", lat: 30.2672, lng: -97.7431 },
  { city: "San Francisco", country: "USA", lat: 37.7749, lng: -122.4194 },
  { city: "Seattle", country: "USA", lat: 47.6062, lng: -122.3321 },
  { city: "Boston", country: "USA", lat: 42.3601, lng: -71.0589 },
  { city: "Washington, D.C.", country: "USA", lat: 38.9072, lng: -77.0369 },
  { city: "Toronto", country: "Canada", lat: 43.65107, lng: -79.347015 },
  { city: "Vancouver", country: "Canada", lat: 49.2827, lng: -123.1207 },
  { city: "Montreal", country: "Canada", lat: 45.5017, lng: -73.5673 },
  { city: "Calgary", country: "Canada", lat: 51.0447, lng: -114.0719 },
  { city: "Ottawa", country: "Canada", lat: 45.4215, lng: -75.6972 },
  { city: "Edmonton", country: "Canada", lat: 53.5461, lng: -113.4938 },
  { city: "Quebec City", country: "Canada", lat: 46.8139, lng: -71.2080 },
  { city: "London", country: "UK", lat: 51.5074, lng: -0.1278 },
  { city: "Paris", country: "France", lat: 48.8566, lng: 2.3522 },
  { city: "Berlin", country: "Germany", lat: 52.5200, lng: 13.4050 },
  { city: "Madrid", country: "Spain", lat: 40.4168, lng: -3.7038 },
  { city: "Rome", country: "Italy", lat: 41.9028, lng: 12.4964 },
  { city: "Tokyo", country: "Japan", lat: 35.6762, lng: 139.6503 },
  { city: "Osaka", country: "Japan", lat: 34.6937, lng: 135.5023 },
  { city: "Seoul", country: "South Korea", lat: 37.5665, lng: 126.9780 },
  { city: "Beijing", country: "China", lat: 39.9042, lng: 116.4074 },
  { city: "Shanghai", country: "China", lat: 31.2304, lng: 121.4737 },
  { city: "Shenzhen", country: "China", lat: 22.5431, lng: 114.0579 },
  { city: "Guangzhou", country: "China", lat: 23.1291, lng: 113.2644 },
  { city: "Hong Kong", country: "China", lat: 22.3193, lng: 114.1694 },
  { city: "Taipei", country: "Taiwan", lat: 25.0330, lng: 121.5654 },
  { city: "New Delhi", country: "India", lat: 28.6139, lng: 77.2090 },
  { city: "Mumbai", country: "India", lat: 19.0760, lng: 72.8777 },
  { city: "Bangalore", country: "India", lat: 12.9716, lng: 77.5946 },
  { city: "Hyderabad", country: "India", lat: 17.3850, lng: 78.4867 },
  { city: "Chennai", country: "India", lat: 13.0827, lng: 80.2707 },
  { city: "Kolkata", country: "India", lat: 22.5726, lng: 88.3639 },
  { city: "Bangkok", country: "Thailand", lat: 13.7563, lng: 100.5018 },
  { city: "Kuala Lumpur", country: "Malaysia", lat: 3.1390, lng: 101.6869 },
  { city: "Jakarta", country: "Indonesia", lat: -6.2088, lng: 106.8456 },
  { city: "Manila", country: "Philippines", lat: 14.5995, lng: 120.9842 },
  { city: "Ho Chi Minh City", country: "Vietnam", lat: 10.8231, lng: 106.6297 },
  { city: "Dubai", country: "UAE", lat: 25.2048, lng: 55.2708 },
  { city: "Abu Dhabi", country: "UAE", lat: 24.4539, lng: 54.3773 },
  { city: "Doha", country: "Qatar", lat: 25.2854, lng: 51.5310 },
  { city: "Riyadh", country: "Saudi Arabia", lat: 24.7136, lng: 46.6753 },
  { city: "Istanbul", country: "Turkey", lat: 41.0082, lng: 28.9784 },
  { city: "Sydney", country: "Australia", lat: -33.8688, lng: 151.2093 },
  { city: "Melbourne", country: "Australia", lat: -37.8136, lng: 144.9631 },
  { city: "Cape Town", country: "South Africa", lat: -33.9249, lng: 18.4241 },
  { city: "Johannesburg", country: "South Africa", lat: -26.2041, lng: 28.0473 },
  { city: "Sao Paulo", country: "Brazil", lat: -23.5505, lng: -46.6333 },
  { city: "Buenos Aires", country: "Argentina", lat: -34.6037, lng: -58.3816 },
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function WeatherCustomizationModal({ open, onClose, userId, brandColor, brandFont, onSave }) {
  const fontOptions = useFontFamilies('option');
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [addNewCity, setAddNewCity] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);

  useEffect(() => {
    if (open && userId) {
      fetch(`/api/weather-settings/get/${userId}`)
        .then(r => r.json())
        .then(data => {
          if (data?.userId) {
            setSettings({ ...DEFAULT_SETTINGS, ...data });
            // If saved coords don't match any known city, switch to manual mode
            if (data.latitude && data.longitude) {
              const match = CITIES.find(c => Math.abs(c.lat - data.latitude) < 0.001 && Math.abs(c.lng - data.longitude) < 0.001);
              setAddNewCity(!match);
            }
          }
        })
        .catch(() => { });
    }
  }, [open, userId]);

  const set = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await fetch(`/api/weather-settings/save/${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      if (onSave) onSave(settings);
      onClose();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    const arr = [...settings.widgetOrder];
    const item = arr.splice(dragIdx, 1)[0];
    arr.splice(idx, 0, item);
    set('widgetOrder', arr);
    setDragIdx(idx);
  };
  const onDragEnd = () => setDragIdx(null);

  // Close city dropdown on outside click
  useEffect(() => {
    if (!cityDropdownOpen) return;
    const handler = () => setCityDropdownOpen(false);
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [cityDropdownOpen]);

  const accentColor = brandColor || '#6366f1';

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="weather-widget-modal"
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
    >
      <ModalContent>
        {/* ── Header ── */}
        <Header>
          <Box>
            <Typography variant="h6" fontWeight={700} color="white" lineHeight={1.2}>
              🌤 Weather Widget
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.6)">
              Customize appearance, position & data
            </Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white', background: 'rgba(255,255,255,0.1)' } }}>
            <CloseIcon />
          </IconButton>
        </Header>

        <Body>
          {/* ── Live Preview ── */}
          <Box sx={{
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            p: 3,
            mb: 3,
            overflowX: 'auto',
            '&::-webkit-scrollbar': { height: '6px' },
            '&::-webkit-scrollbar-track': { background: '#f1f5f9', borderRadius: '3px' },
            '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '3px' },
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 'min-content', pb: 1 }}>
              <WeatherWidget
                apiKey={settings.weatherApiKey}
                latitude={settings.latitude}
                longitude={settings.longitude}
                forecastDays={settings.forecastDays}
                temperatureUnit={settings.temperatureUnit}
                celsiusFirst={settings.celsiusFirst}
                showLocation={settings.showLocation}
                showClock={settings.showClock}
                showCurrentWeather={settings.showCurrentWeather}
                showForecast={settings.showForecast}
                widgetOrder={settings.widgetOrder}
                backgroundColor={settings.backgroundColor}
                backgroundOpacity={settings.backgroundOpacity}
                textColor={settings.textColor}
                subTextColor={settings.subTextColor}
                padding={settings.padding}
                borderRadius={settings.borderRadius}
                width={settings.width}
                fontFamily={settings.fontFamily === 'inherit' ? brandFont || 'inherit' : settings.fontFamily}
                fontSize={settings.fontSize}
                iconSize={settings.iconSize}
                forecastIconSize={settings.forecastIconSize}
                shadowH={settings.shadowH}
                shadowV={settings.shadowV}
                shadowBlur={settings.shadowBlur}
                shadowSpread={settings.shadowSpread}
                shadowColor={settings.shadowColor}
                shadowOpacity={settings.shadowOpacity}
              />
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* ── LEFT COLUMN ── */}
            <Grid item xs={12} md={6}>

              <SectionCard>
                <SectionHeading>📍 Location</SectionHeading>

                {/* Add New City checkbox */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: '8px 12px', background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '8px' }}>
                  <input
                    type="checkbox"
                    id="add-new-city"
                    checked={addNewCity}
                    onChange={e => {
                      setAddNewCity(e.target.checked);
                      if (!e.target.checked) {
                        // switching back to city selector — clear manual coords
                        set('latitude', null);
                        set('longitude', null);
                      }
                    }}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor }}
                  />
                  <label htmlFor="add-new-city" style={{ fontSize: '13px', fontWeight: 600, color: '#374151', cursor: 'pointer', userSelect: 'none' }}>
                    Add New City (enter coordinates manually)
                  </label>
                </Box>

                {/* City selector — shown when addNewCity is OFF */}
                {!addNewCity && (
                  <Box sx={{ position: 'relative', mb: 1 }}>
                    <FieldLabel>Select City</FieldLabel>
                    <Box
                      onMouseDown={e => e.stopPropagation()}
                      onClick={() => setCityDropdownOpen(prev => !prev)}
                      sx={{
                        width: '100%', padding: '8px 10px', borderRadius: '8px',
                        border: `1.5px solid ${cityDropdownOpen ? accentColor : '#e2e8f0'}`,
                        fontSize: '13px', cursor: 'pointer', background: 'white',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        boxSizing: 'border-box', userSelect: 'none',
                      }}
                    >
                      <span style={{ color: settings.latitude ? '#1a202c' : '#9ca3af' }}>
                        {settings.latitude
                          ? (() => {
                            const match = CITIES.find(c => Math.abs(c.lat - settings.latitude) < 0.001 && Math.abs(c.lng - settings.longitude) < 0.001);
                            return match ? `${match.city}, ${match.country}` : `${settings.latitude}, ${settings.longitude}`;
                          })()
                          : 'Search and select a city…'}
                      </span>
                      <span style={{ fontSize: '10px', color: '#94a3b8' }}>{cityDropdownOpen ? '▲' : '▼'}</span>
                    </Box>

                    {cityDropdownOpen && (
                      <Box
                        onMouseDown={e => e.stopPropagation()}
                        sx={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 999,
                          background: 'white', border: '1.5px solid #e2e8f0', borderRadius: '8px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', mt: '4px', overflow: 'hidden',
                        }}
                      >
                        <Box sx={{ p: 1, borderBottom: '1px solid #f1f5f9' }}>
                          <input
                            autoFocus
                            type="text"
                            placeholder="Search city or country…"
                            value={citySearch}
                            onChange={e => setCitySearch(e.target.value)}
                            onClick={e => e.stopPropagation()}
                            style={{
                              width: '100%', padding: '7px 10px', borderRadius: '6px',
                              border: '1.5px solid #e2e8f0', fontSize: '13px', outline: 'none',
                              boxSizing: 'border-box',
                            }}
                          />
                        </Box>
                        <Box sx={{ maxHeight: '220px', overflowY: 'auto' }}>
                          {CITIES.filter(c =>
                            c.city.toLowerCase().includes(citySearch.toLowerCase()) ||
                            c.country.toLowerCase().includes(citySearch.toLowerCase())
                          ).map((c, i) => (
                            <Box
                              key={i}
                              onClick={() => {
                                set('latitude', c.lat);
                                set('longitude', c.lng);
                                setCityDropdownOpen(false);
                                setCitySearch('');
                              }}
                              sx={{
                                px: 2, py: '9px', cursor: 'pointer', fontSize: '13px',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                background: (settings.latitude && Math.abs(c.lat - settings.latitude) < 0.001) ? `${accentColor}12` : 'transparent',
                                color: (settings.latitude && Math.abs(c.lat - settings.latitude) < 0.001) ? accentColor : '#1a202c',
                                fontWeight: (settings.latitude && Math.abs(c.lat - settings.latitude) < 0.001) ? 600 : 400,
                                '&:hover': { background: '#f8fafc' },
                              }}
                            >
                              <span>{c.city}</span>
                              <span style={{ fontSize: '11px', color: '#94a3b8' }}>{c.country}</span>
                            </Box>
                          ))}
                          {CITIES.filter(c =>
                            c.city.toLowerCase().includes(citySearch.toLowerCase()) ||
                            c.country.toLowerCase().includes(citySearch.toLowerCase())
                          ).length === 0 && (
                              <Box sx={{ px: 2, py: 2, fontSize: '13px', color: '#94a3b8', textAlign: 'center' }}>
                                No cities found
                              </Box>
                            )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Manual lat/lng — shown when addNewCity is ON */}
                {addNewCity && (
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FieldLabel>Latitude</FieldLabel>
                      <StyledInput type="number" value={settings.latitude ?? ''} placeholder="e.g. 40.7128"
                        onChange={e => set('latitude', e.target.value === '' ? null : parseFloat(e.target.value))} />
                    </Grid>
                    <Grid item xs={6}>
                      <FieldLabel>Longitude</FieldLabel>
                      <StyledInput type="number" value={settings.longitude ?? ''} placeholder="e.g. -74.0060"
                        onChange={e => set('longitude', e.target.value === '' ? null : parseFloat(e.target.value))} />
                    </Grid>
                  </Grid>
                )}

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {addNewCity ? 'Enter coordinates manually. Leave blank to use browser geolocation.' : 'Select a city from the list, or check "Add New City" to enter coordinates manually.'}
                </Typography>
              </SectionCard>

              <SectionCard>
                <SectionHeading>📅 Forecast</SectionHeading>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FieldLabel>Forecast Days (1–5)</FieldLabel>
                    <StyledInput type="number" value={settings.forecastDays} min={1} max={5}
                      onChange={e => set('forecastDays', Math.min(7, Math.max(1, parseInt(e.target.value) || 1)))} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel>Temperature Unit</FieldLabel>
                    <StyledSelect value={settings.temperatureUnit} onChange={e => set('temperatureUnit', e.target.value)}>
                      <option value="both">Both (°C & °F)</option>
                      <option value="celsius">Celsius Only</option>
                      <option value="fahrenheit">Fahrenheit Only</option>
                    </StyledSelect>
                  </Grid>
                  {settings.temperatureUnit === 'both' && (
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Switch checked={settings.celsiusFirst} onChange={e => set('celsiusFirst', e.target.checked)} size="small"
                          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: accentColor }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accentColor } }} />}
                        label={<Typography variant="body2">Show Celsius first</Typography>}
                      />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>

              <SectionCard>
                <SectionHeading>👁 Visibility</SectionHeading>
                {[
                  { key: 'showClock', label: 'Show Clock / Time', desc: 'Displays HH:MM AM/PM' },
                  { key: 'showLocation', label: 'Show Location Name', desc: 'Displays city & country' },
                  { key: 'showCurrentWeather', label: 'Show Current Weather', desc: 'Temperature & condition' },
                  { key: 'showForecast', label: 'Show Forecast', desc: 'Multi-day forecast' },
                ].map(({ key, label, desc }) => (
                  <ToggleRow key={key}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{label}</Typography>
                      <Typography variant="caption" color="text.secondary">{desc}</Typography>
                    </Box>
                    <Switch checked={settings[key]} onChange={e => set(key, e.target.checked)} size="small"
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: accentColor }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accentColor } }} />
                  </ToggleRow>
                ))}
              </SectionCard>

              <SectionCard>
                <SectionHeading>📱 Screen Visibility</SectionHeading>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                  Control where the weather widget appears in your application
                </Typography>
                {[
                  { key: 'splashScreen', label: 'Splash Screen', desc: 'Show on splash screen' },
                  { key: 'mainMenu', label: 'Main Menu', desc: 'Show on main menu screen' },
                  { key: 'subMenu', label: 'Sub Menu', desc: 'Show on submenu screens' },
                  { key: 'wayfinderSearchScreen', label: 'Wayfinder Search', desc: 'Show on wayfinder search screen' },
                  { key: 'avatarScreen', label: 'Avatar Screen', desc: 'Show on avatar/location screen' },
                ].map(({ key, label, desc }) => (
                  <ToggleRow key={key}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{label}</Typography>
                      <Typography variant="caption" color="text.secondary">{desc}</Typography>
                    </Box>
                    <Switch
                      checked={settings.visibility?.[key] ?? true}
                      onChange={e => set('visibility', { ...settings.visibility, [key]: e.target.checked })}
                      size="small"
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: accentColor }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: accentColor } }}
                    />
                  </ToggleRow>
                ))}
              </SectionCard>

              <SectionCard>
                <SectionHeading>
                  <DragIndicatorIcon sx={{ fontSize: 14 }} /> Section Order
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                    Drag to reorder
                  </Typography>
                </SectionHeading>
                {settings.widgetOrder.map((section, idx) => {
                  const meta = SECTION_META[section] || { label: section, emoji: '•', color: '#94a3b8' };
                  return (
                    <OrderItem key={section} isDragging={dragIdx === idx} draggable
                      onDragStart={() => onDragStart(idx)} onDragOver={e => onDragOver(e, idx)} onDragEnd={onDragEnd}>
                      <DragIndicatorIcon sx={{ color: '#94a3b8', fontSize: 18, flexShrink: 0 }} />
                      <Chip label={idx + 1} size="small" sx={{ background: meta.color, color: 'white', fontWeight: 700, minWidth: 24, height: 20, fontSize: 11 }} />
                      <span style={{ fontSize: '16px' }}>{meta.emoji}</span>
                      <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>{meta.label}</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" disabled={idx === 0} onClick={() => {
                          const arr = [...settings.widgetOrder];[arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]]; set('widgetOrder', arr);
                        }} sx={{ width: 24, height: 24, fontSize: 12 }}>↑</IconButton>
                        <IconButton size="small" disabled={idx === settings.widgetOrder.length - 1} onClick={() => {
                          const arr = [...settings.widgetOrder];[arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]]; set('widgetOrder', arr);
                        }} sx={{ width: 24, height: 24, fontSize: 12 }}>↓</IconButton>
                      </Box>
                    </OrderItem>
                  );
                })}
              </SectionCard>
            </Grid>

            {/* ── RIGHT COLUMN ── */}
            <Grid item xs={12} md={6}>

              <SectionCard>
                <SectionHeading>📐 Position (% from edge)</SectionHeading>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  {['left', 'center', 'right'].map(align => (
                    <Box key={align} onClick={() => set('horizontalAlign', align)} sx={{
                      flex: 1, textAlign: 'center', py: '7px', borderRadius: '8px', cursor: 'pointer',
                      fontSize: '13px', fontWeight: 600, userSelect: 'none', transition: 'all 0.15s',
                      border: `1.5px solid ${settings.horizontalAlign === align ? accentColor : '#e2e8f0'}`,
                      background: settings.horizontalAlign === align ? `${accentColor}18` : '#fff',
                      color: settings.horizontalAlign === align ? accentColor : '#64748b',
                    }}>
                      {align === 'left' ? '⬅ Left' : align === 'center' ? '↔ Center' : 'Right ➡'}
                    </Box>
                  ))}
                </Box>
                {settings.horizontalAlign === 'center' && (
                  <Box sx={{ mb: 2, p: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px' }}>
                    <Typography variant="caption" color="success.main" fontWeight={600}>
                      ✓ Widget will be horizontally centered. Left & Right offsets are disabled.
                    </Typography>
                  </Box>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FieldLabel>Top %</FieldLabel>
                    <StyledInput type="number" value={settings.positionTop ?? ''} placeholder="—"
                      onChange={e => set('positionTop', e.target.value === '' ? null : parseFloat(e.target.value))} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel>Bottom %</FieldLabel>
                    <StyledInput type="number" value={settings.positionBottom ?? ''} placeholder="—"
                      onChange={e => set('positionBottom', e.target.value === '' ? null : parseFloat(e.target.value))} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel style={{ opacity: settings.horizontalAlign === 'center' ? 0.4 : 1 }}>Left %</FieldLabel>
                    <StyledInput type="number"
                      value={settings.horizontalAlign === 'center' ? '' : (settings.positionLeft ?? '')}
                      placeholder={settings.horizontalAlign === 'center' ? 'disabled' : '—'}
                      disabled={settings.horizontalAlign === 'center'}
                      style={{ opacity: settings.horizontalAlign === 'center' ? 0.4 : 1, cursor: settings.horizontalAlign === 'center' ? 'not-allowed' : 'auto' }}
                      onChange={e => set('positionLeft', e.target.value === '' ? null : parseFloat(e.target.value))} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel style={{ opacity: settings.horizontalAlign === 'center' ? 0.4 : 1 }}>Right %</FieldLabel>
                    <StyledInput type="number"
                      value={settings.horizontalAlign === 'center' ? '' : (settings.positionRight ?? '')}
                      placeholder={settings.horizontalAlign === 'center' ? 'disabled' : '—'}
                      disabled={settings.horizontalAlign === 'center'}
                      style={{ opacity: settings.horizontalAlign === 'center' ? 0.4 : 1, cursor: settings.horizontalAlign === 'center' ? 'not-allowed' : 'auto' }}
                      onChange={e => set('positionRight', e.target.value === '' ? null : parseFloat(e.target.value))} />
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Set opposing edges (e.g. Top + Right) to anchor the widget
                </Typography>
              </SectionCard>

              <SectionCard>
                <SectionHeading>🎨 Appearance</SectionHeading>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FieldLabel>Background Color</FieldLabel>
                    <MuiColorInput format="hex" value={settings.backgroundColor} onChange={v => set('backgroundColor', v)} isAlphaHidden
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 38 }, width: '100%' }} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel>Text Color</FieldLabel>
                    <MuiColorInput format="hex" value={settings.textColor} onChange={v => set('textColor', v)} isAlphaHidden
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 38 }, width: '100%' }} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel>Sub Text Color</FieldLabel>
                    <MuiColorInput format="hex" value={settings.subTextColor} onChange={v => set('subTextColor', v)} isAlphaHidden
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 38 }, width: '100%' }} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel>Opacity: {settings.backgroundOpacity}%</FieldLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '6px' }}>
                      <input type="range" min={0} max={100} value={settings.backgroundOpacity}
                        onChange={e => set('backgroundOpacity', parseInt(e.target.value))}
                        style={{ flex: 1, accentColor }} />
                      <Typography variant="caption" fontWeight={600} sx={{ minWidth: 30 }}>{settings.backgroundOpacity}%</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <FieldLabel>Padding (px)</FieldLabel>
                    <StyledInput type="number" value={settings.padding} min={0} onChange={e => set('padding', parseInt(e.target.value) || 0)} />
                  </Grid>
                  <Grid item xs={4}>
                    <FieldLabel>Radius (px)</FieldLabel>
                    <StyledInput type="number" value={settings.borderRadius} min={0} onChange={e => set('borderRadius', parseInt(e.target.value) || 0)} />
                  </Grid>
                  <Grid item xs={4}>
                    <FieldLabel>Width (0=auto)</FieldLabel>
                    <StyledInput type="number" value={settings.width} min={0} onChange={e => set('width', parseInt(e.target.value) || 0)} />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard>
                <SectionHeading>✏️ Typography & Icons</SectionHeading>
                <Grid container spacing={2}>
                  <Grid item xs={8}>
                    <FieldLabel>Font Family</FieldLabel>
                    <StyledSelect value={settings.fontFamily} onChange={e => set('fontFamily', e.target.value)}>
                      <option value="inherit">Use Brand Font</option>
                      {fontOptions}
                    </StyledSelect>
                  </Grid>
                  <Grid item xs={4}>
                    <FieldLabel>Font Size (px)</FieldLabel>
                    <StyledInput type="number" value={settings.fontSize} min={8} max={48} onChange={e => set('fontSize', parseInt(e.target.value) || 14)} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel>Weather Icon (px)</FieldLabel>
                    <StyledInput type="number" value={settings.iconSize} min={16} max={120} onChange={e => set('iconSize', parseInt(e.target.value) || 40)} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel>Forecast Icon (px)</FieldLabel>
                    <StyledInput type="number" value={settings.forecastIconSize} min={12} max={80} onChange={e => set('forecastIconSize', parseInt(e.target.value) || 28)} />
                  </Grid>
                </Grid>
              </SectionCard>

              <SectionCard>
                <SectionHeading>🌑 Box Shadow</SectionHeading>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FieldLabel>Shadow Color</FieldLabel>
                    <MuiColorInput format="hex" value={settings.shadowColor} onChange={v => set('shadowColor', v)} isAlphaHidden
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 38 }, width: '100%' }} />
                  </Grid>
                  <Grid item xs={6}>
                    <FieldLabel>Opacity: {settings.shadowOpacity}%</FieldLabel>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: '6px' }}>
                      <input type="range" min={0} max={100} value={settings.shadowOpacity}
                        onChange={e => set('shadowOpacity', parseInt(e.target.value))}
                        style={{ flex: 1, accentColor }} />
                      <Typography variant="caption" fontWeight={600} sx={{ minWidth: 30 }}>{settings.shadowOpacity}%</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <FieldLabel>H (px)</FieldLabel>
                    <StyledInput type="number" value={settings.shadowH} onChange={e => set('shadowH', parseInt(e.target.value) || 0)} />
                  </Grid>
                  <Grid item xs={3}>
                    <FieldLabel>V (px)</FieldLabel>
                    <StyledInput type="number" value={settings.shadowV} onChange={e => set('shadowV', parseInt(e.target.value) || 0)} />
                  </Grid>
                  <Grid item xs={3}>
                    <FieldLabel>Blur (px)</FieldLabel>
                    <StyledInput type="number" value={settings.shadowBlur} min={0} onChange={e => set('shadowBlur', parseInt(e.target.value) || 0)} />
                  </Grid>
                  <Grid item xs={3}>
                    <FieldLabel>Spread (px)</FieldLabel>
                    <StyledInput type="number" value={settings.shadowSpread} onChange={e => set('shadowSpread', parseInt(e.target.value) || 0)} />
                  </Grid>
                </Grid>
              </SectionCard>

            </Grid>
          </Grid>
        </Body>

        {/* ── Footer ── */}
        <Footer>
          <Button variant="outlined" onClick={onClose}
            sx={{ borderRadius: '8px', textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#94a3b8' } }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700, px: 3, background: accentColor, '&:hover': { background: accentColor, filter: 'brightness(0.9)' } }}>
            {saving ? 'Saving…' : '💾 Save Settings'}
          </Button>
        </Footer>
      </ModalContent>
    </Modal>
  );
}
